import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { villageInfants, recentDonors, donationStats, donationPackages } from "@/mocks/donation";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.crownhongyunmall.com";

const donationJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "爱心村童捐赠计划 | 皇冠鸿运商城",
    "description": "为偏远山村婴儿送去奶粉、图书和爱心礼物。捐赠后收到收据和婴儿照片，这些孩子将用一生感谢您的善良。",
    "url": `${SITE_URL}/donation`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "爱心村童捐赠", "item": `${SITE_URL}/donation` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "DonateAction",
    "name": "爱心村童捐赠计划",
    "description": "欢迎彩票中奖者、富有爱心的成功人士为偏远山村婴儿捐赠奶粉、图书和爱心礼物。",
    "url": `${SITE_URL}/donation`,
    "recipient": {
      "@type": "Organization",
      "name": "皇冠鸿运商城爱心村童捐赠计划"
    }
  }
];

type TabType = "infants" | "donate" | "donors" | "stories";

export default function DonationPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("infants");
  const [selectedInfant, setSelectedInfant] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [donorMessage, setDonorMessage] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [animCount, setAnimCount] = useState(0);
  const [showThankModal, setShowThankModal] = useState(false);
  const [thankInfant, setThankInfant] = useState<typeof villageInfants[0] | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimCount((prev) => (prev + 1) % recentDonors.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const handleDonate = (e: React.FormEvent) => {
    e.preventDefault();
    const infant = villageInfants.find((i) => i.id === selectedInfant);
    setThankInfant(infant || null);
    setSubmitSuccess(true);
    setShowThankModal(true);
  };

  const getStatusBadge = (status: string) => {
    if (status === "urgent") return <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-medium">紧急需要</span>;
    if (status === "new") return <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full font-medium">新加入</span>;
    return <span className="bg-green-100 text-green-600 text-xs px-2 py-0.5 rounded-full font-medium">募集中</span>;
  };

  const selectedPkg = donationPackages.find((p) => p.id === selectedPackage);
  const donationAmount = selectedPackage === "pkg4" ? Number(customAmount) : (selectedPkg?.price || 0);

  return (
    <div className="min-h-screen bg-[#fffaf7]" style={{ fontFamily: "'Noto Sans SC', sans-serif" }}>
      <SeoHead
        title="爱心村童捐赠计划 | 为山村婴儿送去奶粉与书籍 | 皇冠鸿运商城"
        description="皇冠鸿运商城爱心村童捐赠计划，欢迎彩票中奖者、成功人士为偏远山村婴儿捐赠奶粉、图书和爱心礼物。捐赠后收到收据和婴儿照片，这些孩子将用一生感谢您的善良。"
        keywords="爱心捐赠,村童捐赠,山村婴儿,公益捐款,感恩教育,爱心礼物"
        canonical="/donation"
        jsonLd={donationJsonLd}
      />
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ minHeight: "480px" }}>
        <img
          src="https://public.readdy.ai/ai/img_res/82331fb53a35f3b1ff758069f0d84395.jpg"
          alt="村童爱心捐赠"
          className="w-full h-full object-cover object-top absolute inset-0"
          style={{ minHeight: "480px" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-12">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </button>
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-500 rounded-full">
                <i className="ri-heart-fill text-white text-2xl"></i>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              爱心村童捐赠计划
            </h1>
            <p className="text-white/90 text-lg leading-relaxed mb-2">
              欢迎彩票中奖者、富有爱心的成功人士加入我们的大家庭
            </p>
            <p className="text-white/75 text-base leading-relaxed mb-8">
              为偏远山村的婴儿送去奶粉、图书和爱心礼物<br />
              我们将把您的捐赠收据和婴儿收到礼物的照片发送到您的邮箱<br />
              <strong className="text-yellow-300">这些孩子将用一生感谢您的善良</strong>
            </p>
            {/* Live donation ticker */}
            <div className="bg-white/15 backdrop-blur-sm rounded-full px-5 py-2.5 inline-flex items-center gap-3 mb-8">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-white text-sm">
                <strong className="text-yellow-300">{recentDonors[animCount].name}</strong>
                {" "}刚刚捐赠了 <strong className="text-yellow-300">¥{recentDonors[animCount].amount.toLocaleString()}</strong>
              </span>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {[
                { label: "爱心捐赠者", value: donationStats.totalDonors.toLocaleString(), icon: "ri-user-heart-line" },
                { label: "累计捐赠额", value: `¥${(donationStats.totalAmount / 10000).toFixed(0)}万`, icon: "ri-money-cny-circle-line" },
                { label: "受助婴儿", value: donationStats.infantsHelped, icon: "ri-emotion-happy-line" },
                { label: "礼物已送达", value: donationStats.giftsDelivered.toLocaleString(), icon: "ri-gift-line" },
              ].map((s) => (
                <div key={s.label} className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center">
                  <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1">
                    <i className={`${s.icon} text-yellow-300 text-lg`}></i>
                  </div>
                  <div className="text-white font-bold text-xl">{s.value}</div>
                  <div className="text-white/70 text-xs">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-0 overflow-x-auto">
            {[
              { id: "infants", label: "需要帮助的婴儿", icon: "ri-emotion-line" },
              { id: "donate", label: "立即捐赠", icon: "ri-heart-line" },
              { id: "donors", label: "爱心捐赠者", icon: "ri-user-heart-line" },
              { id: "stories", label: "感恩故事", icon: "ri-book-open-line" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-red-500 text-red-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
                {tab.id === "donate" && (
                  <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full animate-pulse">爱心</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* INFANTS TAB */}
        {activeTab === "infants" && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                他们在等待您的爱心
              </h2>
              <p className="text-gray-500 text-sm">每一个婴儿都是天使，您的一份爱心将改变他们的一生</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {villageInfants.map((infant) => {
                const progress = Math.round((infant.raisedAmount / infant.goalAmount) * 100);
                return (
                  <div key={infant.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-red-200 transition-all group">
                    <div className="relative overflow-hidden" style={{ height: "220px" }}>
                      <img
                        src={infant.image}
                        alt={infant.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">{getStatusBadge(infant.status)}</div>
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 text-xs text-gray-600 font-medium">
                        <i className="ri-map-pin-line text-red-500 mr-1"></i>{infant.province}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <div className="text-white font-bold text-lg">{infant.name}</div>
                        <div className="text-white/80 text-xs">{infant.age} · {infant.village}</div>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-gray-600 text-sm leading-relaxed mb-3 line-clamp-3">{infant.story}</p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {infant.needs.map((need) => (
                          <span key={need} className="bg-red-50 text-red-600 text-xs px-2 py-0.5 rounded-full">
                            <i className="ri-heart-line mr-1"></i>{need}
                          </span>
                        ))}
                      </div>
                      {/* Progress */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                          <span>已募集 ¥{infant.raisedAmount.toLocaleString()}</span>
                          <span>目标 ¥{infant.goalAmount.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between text-xs mt-1">
                          <span className="text-red-500 font-medium">{progress}% 已完成</span>
                          <span className="text-gray-400">{infant.donors} 位爱心人士</span>
                        </div>
                      </div>
                      {infant.receivedGifts.length > 0 && (
                        <div className="bg-green-50 rounded-lg p-2 mb-3">
                          <div className="text-green-700 text-xs font-medium mb-1">
                            <i className="ri-checkbox-circle-line mr-1"></i>已收到礼物
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {infant.receivedGifts.map((gift) => (
                              <span key={gift} className="text-green-600 text-xs bg-green-100 px-1.5 py-0.5 rounded">{gift}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => { setSelectedInfant(infant.id); setActiveTab("donate"); }}
                        className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
                      >
                        <i className="ri-heart-line mr-2"></i>为{infant.name}捐赠
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* DONATE TAB */}
        {activeTab === "donate" && (
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                献出您的爱心
              </h2>
              <p className="text-gray-500 text-sm">捐赠后我们将把收据和婴儿收到礼物的照片发送到您的邮箱</p>
            </div>

            {/* Select infant */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 mb-5">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full text-xs font-bold">1</span>
                选择您想帮助的婴儿
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {villageInfants.map((infant) => (
                  <button
                    key={infant.id}
                    onClick={() => setSelectedInfant(infant.id)}
                    className={`relative rounded-xl overflow-hidden cursor-pointer transition-all border-2 ${
                      selectedInfant === infant.id ? "border-red-500" : "border-transparent"
                    }`}
                    style={{ height: "100px" }}
                  >
                    <img src={infant.image} alt={infant.name} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="text-white font-bold text-sm">{infant.name}</div>
                      <div className="text-white/70 text-xs">{infant.province}</div>
                    </div>
                    {selectedInfant === infant.id && (
                      <div className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center bg-red-500 rounded-full">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    )}
                  </button>
                ))}
                <button
                  onClick={() => setSelectedInfant("any")}
                  className={`rounded-xl border-2 cursor-pointer transition-all flex flex-col items-center justify-center gap-1 ${
                    selectedInfant === "any" ? "border-red-500 bg-red-50" : "border-dashed border-gray-200 hover:border-red-300"
                  }`}
                  style={{ height: "100px" }}
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-heart-line text-red-400 text-2xl"></i>
                  </div>
                  <span className="text-xs text-gray-500 text-center leading-tight">随机帮助<br />最需要的婴儿</span>
                </button>
              </div>
            </div>

            {/* Select package */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 mb-5">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full text-xs font-bold">2</span>
                选择捐赠礼包
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {donationPackages.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg.id)}
                    className={`relative text-left rounded-xl p-4 border-2 cursor-pointer transition-all ${
                      selectedPackage === pkg.id ? "border-red-500 bg-red-50" : "border-gray-100 hover:border-red-200"
                    }`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">最受欢迎</div>
                    )}
                    <div className={`w-10 h-10 flex items-center justify-center bg-gradient-to-br ${pkg.color} rounded-xl mb-2`}>
                      <i className={`${pkg.icon} text-white text-lg`}></i>
                    </div>
                    <div className="font-bold text-gray-900 text-sm mb-0.5">{pkg.name}</div>
                    {pkg.price > 0 && (
                      <div className="text-red-600 font-bold text-lg mb-1">¥{pkg.price}</div>
                    )}
                    <div className="text-gray-500 text-xs mb-2">{pkg.desc}</div>
                    <ul className="space-y-0.5">
                      {pkg.items.map((item) => (
                        <li key={item} className="text-xs text-gray-600 flex items-center gap-1">
                          <i className="ri-check-line text-green-500 text-xs flex-shrink-0"></i>
                          {item}
                        </li>
                      ))}
                    </ul>
                    {pkg.id === "pkg4" && selectedPackage === "pkg4" && (
                      <div className="mt-3">
                        <input
                          type="number"
                          min="10"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          placeholder="请输入金额（元）"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Donor info form */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 mb-5">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full text-xs font-bold">3</span>
                您的信息（用于发送收据和婴儿照片）
              </h3>
              <form id="donation-form" data-readdy-form onSubmit={handleDonate} className="space-y-4">
                <input type="hidden" name="infant_id" value={selectedInfant || ""} />
                <input type="hidden" name="package_id" value={selectedPackage || ""} />
                <input type="hidden" name="amount" value={donationAmount} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      您的姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="donor_name"
                      required
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      placeholder="请输入您的姓名"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      邮箱地址 <span className="text-red-500">*</span>
                      <span className="text-gray-400 font-normal ml-1 text-xs">（收据和婴儿照片将发送至此）</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      placeholder="请输入您的邮箱"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    给婴儿的祝福语
                    <span className="text-gray-400 font-normal ml-1 text-xs">（可选，将附在礼物卡片上）</span>
                  </label>
                  <textarea
                    name="message"
                    value={donorMessage}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) setDonorMessage(e.target.value);
                    }}
                    placeholder="写下您对这个小宝贝的祝福，让他/她感受到您的爱..."
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors resize-none"
                  />
                  <div className="text-right text-xs text-gray-400 mt-1">{donorMessage.length}/500</div>
                </div>

                {/* Summary */}
                {selectedPackage && (selectedPackage !== "pkg4" || customAmount) && (
                  <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 text-sm font-medium">捐赠礼包</span>
                      <span className="text-gray-900 text-sm">{selectedPkg?.name}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 text-sm font-medium">捐赠金额</span>
                      <span className="text-red-600 font-bold text-lg">¥{donationAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-mail-send-line text-red-400"></i>
                      捐赠成功后，收据和婴儿收到礼物的照片将发送至您的邮箱
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!selectedInfant || !selectedPackage || (selectedPackage === "pkg4" && !customAmount)}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white py-3.5 rounded-xl text-base font-bold cursor-pointer transition-all whitespace-nowrap"
                >
                  <i className="ri-heart-fill mr-2"></i>
                  立即捐赠，传递爱心
                </button>
                <p className="text-center text-xs text-gray-400">
                  <i className="ri-shield-check-line mr-1 text-green-500"></i>
                  100% 善款用于购买物资 · 全程透明公示 · 捐赠收据即时发送
                </p>
              </form>
            </div>
          </div>
        )}

        {/* DONORS TAB */}
        {activeTab === "donors" && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                爱心捐赠者荣誉榜
              </h2>
              <p className="text-gray-500 text-sm">感谢每一位有爱心的朋友，您的善举将被永远铭记</p>
            </div>

            {/* Top donors highlight */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                { rank: 1, name: "孙**（富商）", amount: 20000, icon: "ri-vip-crown-fill", color: "from-yellow-400 to-amber-500", badge: "至尊爱心大使" },
                { rank: 2, name: "李**（企业家）", amount: 10000, icon: "ri-medal-fill", color: "from-gray-300 to-gray-400", badge: "白金爱心大使" },
                { rank: 3, name: "陈**（TikTok明星）", amount: 8000, icon: "ri-award-fill", color: "from-amber-600 to-amber-700", badge: "黄金爱心大使" },
              ].map((donor) => (
                <div key={donor.rank} className={`bg-gradient-to-br ${donor.color} rounded-2xl p-5 text-white text-center`}>
                  <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                    <i className={`${donor.icon} text-2xl`}></i>
                  </div>
                  <div className="text-2xl font-bold mb-0.5">#{donor.rank}</div>
                  <div className="font-bold text-lg mb-1">{donor.name}</div>
                  <div className="text-white/90 text-xl font-bold mb-2">¥{donor.amount.toLocaleString()}</div>
                  <div className="bg-white/20 rounded-full px-3 py-1 text-xs font-medium">{donor.badge}</div>
                </div>
              ))}
            </div>

            {/* Recent donors list */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
                <h3 className="font-bold text-gray-900">最新捐赠动态</h3>
                <div className="flex items-center gap-1.5 text-green-500 text-xs">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  实时更新
                </div>
              </div>
              <div className="divide-y divide-gray-50">
                {recentDonors.map((donor, idx) => (
                  <div key={idx} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                      <i className={`${donor.avatar} ${donor.color} text-xl`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{donor.name}</div>
                      <div className="text-gray-400 text-xs">{donor.gift}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-red-600 font-bold text-sm">¥{donor.amount.toLocaleString()}</div>
                      <div className="text-gray-400 text-xs">{donor.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Join CTA */}
            <div className="mt-8 bg-gradient-to-r from-red-500 to-red-700 rounded-2xl p-6 text-center text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                <i className="ri-heart-fill text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                加入爱心大家庭
              </h3>
              <p className="text-red-100 text-sm mb-4">
                彩票中奖者、成功企业家、达人明星们<br />
                您的一份爱心，将成为孩子一生中最美好的礼物
              </p>
              <button
                onClick={() => setActiveTab("donate")}
                className="bg-yellow-400 hover:bg-yellow-500 text-red-900 px-8 py-3 rounded-full font-bold cursor-pointer transition-colors whitespace-nowrap"
              >
                立即加入，传递爱心
              </button>
            </div>
          </div>
        )}

        {/* STORIES TAB */}
        {activeTab === "stories" && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                感恩的故事
              </h2>
              <p className="text-gray-500 text-sm">这些孩子将用一生感谢您的善良，我们是一个大家庭</p>
            </div>

            {/* Story cards */}
            <div className="space-y-6">
              {[
                {
                  infantName: "小明",
                  infantAge: "现在14个月",
                  province: "云南",
                  donorName: "王先生（彩票中奖者）",
                  donorAmount: 5000,
                  story: "王先生中了彩票后，第一件事就是来到我们平台为小明捐赠了奶粉和图画书。三个月后，我们收到了小明奶奶发来的视频——小明正在翻看图画书，咯咯笑个不停。王先生说：'这是我这辈子花得最值的钱，看到孩子的笑脸，我比中奖还开心。'",
                  image: "https://readdy.ai/api/search-image?query=happy%20Chinese%20baby%20boy%20reading%20colorful%20picture%20book%2C%20joyful%20expression%2C%20warm%20home%20setting%2C%20grandmother%20watching%20lovingly%2C%20heartwarming%20family%20moment%2C%20soft%20natural%20light%2C%20touching%20and%20beautiful%20scene&width=600&height=400&seq=story001&orientation=landscape",
                  date: "2026年3月",
                  thankNote: "谢谢王叔叔，我会好好读书，将来报答您！",
                },
                {
                  infantName: "小花",
                  infantAge: "现在11个月",
                  province: "贵州",
                  donorName: "陈女士（TikTok达人）",
                  donorAmount: 8000,
                  story: "陈女士在TikTok上看到我们的推广视频后，立即为小花和她的双胞胎姐姐各捐赠了一套成长礼物包。她还在自己的账号上分享了这段经历，带动了粉丝们纷纷加入捐赠。小花的妈妈专门录制了一段感谢视频，看哭了无数网友。",
                  image: "https://readdy.ai/api/search-image?query=adorable%20Chinese%20twin%20baby%20girls%20receiving%20gift%20packages%2C%20happy%20and%20healthy%2C%20rural%20village%20home%2C%20mother%20holding%20babies%20with%20grateful%20expression%2C%20warm%20touching%20scene%2C%20beautiful%20natural%20light&width=600&height=400&seq=story002&orientation=landscape",
                  date: "2026年2月",
                  thankNote: "陈阿姨，我和姐姐会一起努力，长大后也要帮助别人！",
                },
                {
                  infantName: "小强",
                  infantAge: "现在15个月",
                  province: "四川",
                  donorName: "李总（企业家）",
                  donorAmount: 10000,
                  story: "李总是一位成功的企业家，每年都会通过我们平台资助多名山村婴儿。他说：'我从小也是农村出来的，知道那种艰难。现在有能力了，就想帮帮这些孩子。'小强的外公外婆收到礼物后，老泪纵横，说这是他们一辈子收到的最好的礼物。",
                  image: "https://readdy.ai/api/search-image?query=Chinese%20baby%20boy%20with%20elderly%20grandparents%20in%20mountain%20village%2C%20receiving%20gift%20box%20with%20milk%20powder%20and%20books%2C%20emotional%20and%20touching%20moment%2C%20tears%20of%20joy%2C%20warm%20golden%20light%2C%20heartwarming%20rural%20family%20scene&width=600&height=400&seq=story003&orientation=landscape",
                  date: "2026年1月",
                  thankNote: "李爷爷，我会像您一样，长大后成为对社会有用的人！",
                },
              ].map((story, idx) => (
                <div key={idx} className="bg-white rounded-2xl overflow-hidden border border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="relative overflow-hidden" style={{ minHeight: "240px" }}>
                      <img
                        src={story.image}
                        alt={story.infantName}
                        className="w-full h-full object-cover object-top absolute inset-0"
                      />
                    </div>
                    <div className="p-6 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="bg-red-50 text-red-600 text-xs px-2 py-1 rounded-full font-medium">
                            <i className="ri-map-pin-line mr-1"></i>{story.province}
                          </span>
                          <span className="text-gray-400 text-xs">{story.date}</span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                          {story.infantName}的故事 · {story.infantAge}
                        </h3>
                        <div className="text-xs text-gray-500 mb-3">
                          捐赠者：<strong className="text-gray-700">{story.donorName}</strong>
                          <span className="text-red-500 font-bold ml-2">¥{story.donorAmount.toLocaleString()}</span>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4">{story.story}</p>
                      </div>
                      <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-3 border border-red-100">
                        <div className="flex items-start gap-2">
                          <i className="ri-double-quotes-l text-red-300 text-xl flex-shrink-0 mt-0.5"></i>
                          <p className="text-red-700 text-sm italic font-medium">{story.thankNote}</p>
                        </div>
                        <div className="text-right text-xs text-red-400 mt-1">— {story.infantName}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Big family message */}
            <div className="mt-10 text-center bg-gradient-to-b from-red-50 to-pink-50 rounded-2xl p-8 border border-red-100">
              <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
                <i className="ri-home-heart-line text-red-500 text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                我们是一个大家庭
              </h3>
              <p className="text-gray-600 text-base leading-relaxed max-w-2xl mx-auto mb-6">
                无论您是彩票中奖者、成功企业家、还是抖音/TikTok/微信/YouTube上的达人明星，
                当您为一个素未谋面的山村婴儿送去奶粉和书籍的那一刻，
                您就成为了他/她生命中最重要的人。
                <br /><br />
                <strong className="text-red-600">这些孩子将用一生感谢您的善良。</strong>
              </p>
              <button
                onClick={() => setActiveTab("donate")}
                className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-full font-bold cursor-pointer transition-colors whitespace-nowrap text-base"
              >
                <i className="ri-heart-fill mr-2"></i>加入大家庭，传递爱心
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Thank you modal */}
      {showThankModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-br from-red-500 to-red-700 p-6 text-center text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                <i className="ri-heart-fill text-3xl animate-pulse"></i>
              </div>
              <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                感谢您的爱心！
              </h3>
              <p className="text-red-100 text-sm">您的善举将改变一个孩子的命运</p>
            </div>
            <div className="p-6">
              {thankInfant && (
                <div className="flex items-center gap-3 bg-red-50 rounded-xl p-3 mb-4">
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={thankInfant.image} alt={thankInfant.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{thankInfant.name} · {thankInfant.age}</div>
                    <div className="text-gray-500 text-xs">{thankInfant.village}</div>
                    <div className="text-red-500 text-xs mt-1 italic">&ldquo;{thankInfant.thankYouMessage}&rdquo;</div>
                  </div>
                </div>
              )}
              <div className="space-y-2 mb-5">
                {[
                  { icon: "ri-mail-send-line", text: `捐赠收据已发送至 ${donorEmail}` },
                  { icon: "ri-image-line", text: "婴儿收到礼物的照片将在7天内发送到您邮箱" },
                  { icon: "ri-shield-check-line", text: "100% 善款用于购买物资，全程透明公示" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-green-500`}></i>
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
              <button
                onClick={() => { setShowThankModal(false); setActiveTab("stories"); }}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl font-bold cursor-pointer transition-colors whitespace-nowrap"
              >
                查看感恩故事
              </button>
              <button
                onClick={() => setShowThankModal(false)}
                className="w-full mt-2 text-gray-400 hover:text-gray-600 py-2 text-sm cursor-pointer transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
